import React, { useContext, useMemo } from 'react'

import { ActForm, useCheckOperation, UUID } from '@builddocs/data-management'
import {
  AOSRType,
  FILE_STORE_API,
  SecondaryActType,
} from '@builddocs/filestore-api'
import { FeatureFlagContext, useDisclosure } from '@builddocs/system'
import CloseIcon from '@mui/icons-material/Close'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import {
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  IconButton,
  Typography,
  Button,
} from '@mui/material'

import { DownloadActButton } from './DownloadActButton'
import { NotificationModal } from './NotificationModal'
import {
  AOSRJsonType,
  ListsFieldsType,
  SecondaryActJsonType,
} from './Types/dataJSON'
import ZoomButtons from './ZoomButtons'

type OwnProps = {
  tools: 'signatures' | 'comments' | 'edit' | null
  hasSignatures: boolean
  act: AOSRType | SecondaryActType
  mode: 'applications' | 'interactiveMode'
  isFullScreen: boolean
  isDisabledTools: boolean
  canEdit: boolean
  canRegenerate: boolean
  zoom: number
  dataJSON: AOSRJsonType | SecondaryActJsonType
  additionalFields: ListsFieldsType
  onChooseTools: (
    event: React.MouseEvent<HTMLElement>,
    newState: 'signatures' | 'comments' | 'edit' | null,
  ) => void
  onChangeMode: (
    event: React.MouseEvent<HTMLElement>,
    newState: 'applications' | 'interactiveMode' | null,
  ) => void
  onClose: () => void
  onSave: () => void
  onReject: () => void
  onChangeScreen: () => void
  onChangeZoom: (e: 'inc' | 'dec' | number) => void
}

export const InteractiveActButtons = ({
  tools,
  hasSignatures,
  dataJSON,
  additionalFields,
  mode,
  act,
  zoom,
  isFullScreen,
  isDisabledTools,
  canRegenerate,
  onChooseTools,
  onChangeMode,
  onChangeScreen,
  onClose,
  onSave,
  onReject,
  onChangeZoom,
  canEdit,
}: OwnProps) => {
  const { BUILDDOCS1941 } = useContext(FeatureFlagContext)
  const {
    isOpen: isOpenNotificationModal,
    onOpen: onOpenNotificationModal,
    onClose: onCloseNotificationModal,
  } = useDisclosure()
  const {
    isOpen: isOpenEmptyRepresentativesConfirmModal,
    onOpen: onOpenEmptyRepresentativesConfirmModal,
    onClose: onCloseEmptyRepresentativesConfirmModal,
  } = useDisclosure()

  const overwrittenAssignees = useMemo(
    () =>
      additionalFields.assigneesFields.reduce(
        (acc: Record<string, UUID[]>, key: string) => {
          const overwrittenAssignees = dataJSON[key]?.overwrittenAssignees

          if (!overwrittenAssignees) {
            return acc
          }

          return {
            ...acc,
            [key]: Object.keys(overwrittenAssignees),
          }
        },
        {} as Record<string, UUID[]>,
      ),
    [dataJSON, additionalFields],
  )
  const { checkOperation } = useCheckOperation()

  const handleSaveWithCheck = () => {
    for (const key in overwrittenAssignees) {
      if (key === 'representative_other' || key === 'representative_designer') {
        continue
      }
      if (overwrittenAssignees[key].length === 0) {
        onOpenNotificationModal()
        return
      }
    }

    const isEmptyOther =
      'representative_other' in overwrittenAssignees &&
      overwrittenAssignees.representative_other.length === 0

    const isEmptyDesigner =
      'representative_designer' in overwrittenAssignees &&
      overwrittenAssignees.representative_designer.length === 0

    if (isEmptyOther || isEmptyDesigner) {
      onOpenEmptyRepresentativesConfirmModal()
      return
    }

    onSave()
  }

  const handleSaveWithDoubleCheck = () => {
    checkOperation({
      operationId: 'UPDATE_ACT',
      operationCallback: () => {
        handleSaveWithCheck()
      },
    })
  }

  return (
    <>
      <NotificationModal
        message="Вы вручную указали отсутвие участников комиссии в одном из полей акта. Чтобы сохранить акт, заполните это поле."
        open={isOpenNotificationModal}
        title="Внимание"
        onClose={onCloseNotificationModal}
      />
      <NotificationModal
        cancelText="Назад"
        message="Поля «Представитель лица, осуществляющего подготовку проектной документации» или «Иные лица» не заполнены. Сохранить акт без указания представителей в этих полях?"
        okText="Сохранить"
        open={isOpenEmptyRepresentativesConfirmModal}
        title="Внимание"
        onClose={onCloseEmptyRepresentativesConfirmModal}
        onOk={() => {
          onCloseEmptyRepresentativesConfirmModal()
          onSave()
        }}
      />
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Stack alignItems="center" direction="row" spacing={2}>
          <Typography>{act.closingName ?? act.name}</Typography>
          {hasSignatures && act.actType === ActForm.AOSR && (
            <Link
              download={act.closingName ?? act.name + '_signature.sig'}
              href={FILE_STORE_API + `/act-aosr/${act.id}/sig`}
              rel="noreferrer"
              target="_blank"
              underline="none"
            >
              Скачать подписи
            </Link>
          )}

          <ToggleButtonGroup
            exclusive
            aria-label="mode buttons"
            color="primary"
            size="small"
            value={mode}
            onChange={onChangeMode}
          >
            <ToggleButton aria-label="applications" value="applications">
              Приложения
            </ToggleButton>
            <ToggleButton
              aria-label="interactiveMode"
              size="small"
              value="interactiveMode"
            >
              Интерактивный режим
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            aria-label="tools buttons"
            color="primary"
            disabled={isDisabledTools}
            size="small"
            value={tools}
            onChange={onChooseTools}
          >
            <ToggleButton
              aria-label="signatures"
              disabled={mode === 'interactiveMode'}
              value="signatures"
            >
              Подписи
            </ToggleButton>
            <ToggleButton aria-label="comments" value="comments">
              Замечания
            </ToggleButton>
            <ToggleButton
              aria-label="edit"
              disabled={
                mode === 'applications' ||
                !canEdit ||
                (BUILDDOCS1941 &&
                  (act?.approvalStatus === 'SIGNED' ||
                    (act?.approvalStatus === 'SIGNING' &&
                      !!act?.assignees?.find(
                        a => a.signatureState === 'SIGNED',
                      ))))
              }
              value="edit"
            >
              Редактировать
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === 'interactiveMode' && (
            <Stack alignItems="center" direction="row" spacing={1}>
              <ZoomButtons zoom={zoom} onChangeZoom={onChangeZoom} />
              {canRegenerate && (
                <Stack alignItems="center" direction="row" spacing={1}>
                  <Button
                    id="act-view-update-act"
                    size="small"
                    variant="contained"
                    onClick={handleSaveWithDoubleCheck}
                  >
                    Сохранить изменения
                  </Button>
                  <Button
                    id="act-view-reject-update-act"
                    size="small"
                    variant="outlined"
                    onClick={onReject}
                  >
                    Отменить
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
        <Stack alignItems="center" direction="row" spacing={1}>
          <DownloadActButton act={act} />

          <IconButton onClick={onChangeScreen}>
            {isFullScreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
          <IconButton aria-label="close" edge="start" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>
    </>
  )
}

export default InteractiveActButtons
